// pages/ProductListing.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, ArrowLeft, ArrowRight, FilterIcon, } from '../../../public/icons';
import { shopService } from '../../../services/shopService';
import { collectionsService } from '../../../services/collectionsService';
import FilterModal from '../../../component/shop/FilterModal';

const ProductListing = () => {
  const { productSlug, collectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [collections, setCollections] = useState([]);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  
  // UI State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    availability: {
      inStock: false,
      outOfStock: false
    },
    types: {
      country: false,
      theme: false,
      category: false,
      occasion: false
    }
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get the product by slug
        const productResponse = await shopService.getProductBySlug(productSlug);
        const productData = productResponse.data;
        setProduct(productData);

        // Check if we're viewing a specific collection
        if (collectionId) {
          // Fetch specific collection with its children
          const collectionResponse = await collectionsService.getCollectionWithChildren(collectionId);
          setCurrentCollection(collectionResponse.data);
          
          // Get all child collections and standalone products
          const allItems = [
            ...(collectionResponse.data.childCollections?.subCollections || []),
            ...(collectionResponse.data.childCollections?.standaloneProducts || [])
          ];
          setCollections(allItems);

          // Get breadcrumb
          const breadcrumbResponse = await collectionsService.getCollectionBreadcrumb(collectionId);
          setBreadcrumb(breadcrumbResponse.data.path);
        } else {
          // Fetch all parent collections for this product
          const collectionsResponse = await collectionsService.getParentCollections(productData._id);
          setCollections(collectionsResponse.data);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load collections');
      } finally {
        setLoading(false);
      }
    };

    if (productSlug) {
      fetchData();
    }
  }, [productSlug, collectionId]);

  // Filter collections based on search and filters
  const filteredCollections = collections.filter(item => {
    // Search filter
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Availability filter (for standalone products)
    if (filters.availability.inStock || filters.availability.outOfStock) {
      if (item.collectionType === 'standalone') {
        const stockStatus = item.productDetails?.stockQuantity > 0;
        if (filters.availability.inStock && !stockStatus) return false;
        if (filters.availability.outOfStock && stockStatus) return false;
      }
    }

    // Type filter
    const typeFilters = filters.types;
    const hasActiveTypeFilters = Object.values(typeFilters).some(Boolean);
    if (hasActiveTypeFilters && item.type) {
      return typeFilters[item.type];
    }

    return true;
  });

  // Pagination
  const totalItems = filteredCollections.length;
  const totalPagesCalc = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCollections = filteredCollections.slice(startIndex, startIndex + itemsPerPage);

  const handleCollectionClick = (collection) => {
    if (collection.collectionType === 'standalone') {
      // Navigate to standalone product page
      navigate(`/product/standalone/${collection.slug || collection._id}`);
    } else {
      // Navigate to this collection to see its children
      navigate(`/product/${productSlug}/collection/${collection._id}`);
    }
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Go to product root
      navigate(`/product/${productSlug}`);
    } else {
      // Go to specific collection in breadcrumb
      navigate(`/product/${productSlug}/collection/${breadcrumb[index].id}`);
    }
  };

  const hasActiveFilters = () => {
    const hasAvailability = Object.values(filters.availability).some(Boolean);
    const hasTypes = Object.values(filters.types).some(Boolean);
    return hasAvailability || hasTypes;
  };

  const handleFilterChange = (category, subcategory) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: !prev[category][subcategory]
      }
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      availability: { inStock: false, outOfStock: false },
      types: { country: false, theme: false, category: false, occasion: false }
    });
    setCurrentPage(1);
  };

  // Get hover message for flip card
  const getHoverMessage = (item) => {
    if (item.collectionType === 'standalone') {
      return item.productDetails?.shortHighlight || 'Click to view details';
    }
    return `Explore ${item.name} collections`;
  };

  // Get background color for flip card
  const getFlipBackground = () => {
    return "bg-[#2B2A2A]";
  };

  // Get primary image for collection
  const getCollectionImage = (item) => {
    if (item.collectionType === 'standalone') {
      const primaryImage = item.productDetails?.images?.find(img => img.isPrimary);
      return primaryImage?.url || item.productDetails?.images?.[0]?.url || item.coverImage?.url;
    }
    return item.coverImage?.url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1C2A46]"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Page</h2>
          <p className="text-[#6B635D] mb-4">{error || 'Product not found'}</p>
          <button 
            onClick={() => navigate('/shop')}
            className="px-6 py-3 bg-[#1C2A46] text-white rounded-md hover:bg-[#2A3A5A] transition-colors"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* Header Section */}
      <section className="py-10">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-6 text-[#6B635D]">
            <button 
              onClick={() => navigate('/')}
              className="hover:text-[#1C2A46] transition-colors"
            >
              {/* <HomeIcon size={18} /> */}
            </button>
            <ArrowRight size={16} />
            <button 
              onClick={() => navigate('/shop')}
              className="hover:text-[#1C2A46] transition-colors"
            >
              Shop
            </button>
            <ArrowRight size={16} />
            <button 
              onClick={() => handleBreadcrumbClick(-1)}
              className="hover:text-[#1C2A46] transition-colors"
            >
              {product.name}
            </button>
            
            {breadcrumb.map((item, index) => (
              <React.Fragment key={item.id}>
                <ArrowRight size={16} />
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className="hover:text-[#1C2A46] transition-colors"
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
            
            {currentCollection && (
              <>
                <ArrowRight size={16} />
                <span className="text-[#1C2A46] font-medium">{currentCollection.name}</span>
              </>
            )}
          </div>

          {/* Title and Description */}
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold text-[#1C2A46] mb-4">
              {currentCollection?.name || product.name}
            </h1>
            <p className="text-xl text-[#6B635D] font-normal max-w-2xl">
              {currentCollection?.description || product.shortHighlight || 
               `Explore our collection of ${product.name}. Each piece tells a unique story.`}
            </p>
          </div>

          {/* Subtitle and Search/Filter */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div className="mb-4 md:mb-0">
              <h2 className="text-2xl md:text-3xl font-medium text-[#1C2A46]">
                {currentCollection ? 'Collections & Products' : 'Parent Collections'}
              </h2>
              <p className="text-sm text-[#6B635D] mt-1">
                {filteredCollections.length} items available
              </p>
            </div>

            <div className="flex gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="p-3 pr-12 border w-[300px] border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={20} />
                </div>
              </div>
              
              <button 
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2 px-4 py-3 border border-[#DBDBDB] bg-transparent hover:bg-[#F8F8F8] transition-colors"
              >
                <FilterIcon />
                <span className="text-[#1C2A46]">Filter</span>
                {hasActiveFilters() && (
                  <span className="w-2 h-2 bg-[#D4A574] rounded-full"></span>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-4 max-w-7xl">
          {paginatedCollections.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-[#6B635D] mb-4">No items found matching your criteria.</p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-[#1C2A46] text-white rounded-md hover:bg-[#2A3A5A] transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {paginatedCollections.map((item) => {
                  const imageUrl = getCollectionImage(item);
                  const stockStatus = item.collectionType === 'standalone' 
                    ? collectionsService.getStockStatus(item.productDetails?.stockQuantity)
                    : null;

                  return (
                    <div 
                      key={item._id} 
                      className="group cursor-pointer"
                      onClick={() => handleCollectionClick(item)}
                    >
                      <div className="h-full flex flex-col">
                        {/* Flip Card Container */}
                        <div className="aspect-square mb-5 overflow-hidden relative">
                          <div className="flip-card w-full h-full">
                            {/* Front of card */}
                            <div className="flip-card-front w-full h-full">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-[#1C2A46] flex items-center justify-center">
                                  <span className="text-white text-6xl">
                                    {collectionsService.getCollectionTypeIcon(item.type)}
                                  </span>
                                </div>
                              )}
                              
                              {/* Type badge */}
                              <div className="absolute top-3 left-3">
                                <span className="px-3 py-1 bg-white/90 text-xs font-medium rounded-full">
                                  {item.type}
                                </span>
                              </div>

                              {/* Child count badge */}
                              {item.childCollections?.childCount > 0 && (
                                <div className="absolute top-3 right-3">
                                  <span className="px-3 py-1 bg-[#1C2A46] text-white text-xs font-medium rounded-full">
                                    {item.childCollections.childCount} items
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Back of card - Flip side */}
                            <div className={`flip-card-back w-full h-full ${getFlipBackground()} flex items-center justify-center p-6`}>
                              <div className="text-center">
                                <p className="text-white text-xl md:text-2xl font-medium leading-tight mb-4">
                                  {getHoverMessage(item)}
                                </p>
                                {item.collectionType === 'standalone' && item.productDetails?.basePrice && (
                                  <p className="text-[#D4A574] text-lg font-semibold">
                                    {collectionsService.formatPrice(item.productDetails.basePrice)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Info Section */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-1">
                            <h3 className="text-xl font-medium text-[#1C2A46]">
                              {item.name}
                            </h3>
                            {item.collectionType === 'standalone' && stockStatus && (
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${stockStatus.badge}`}>
                                {stockStatus.text}
                              </span>
                            )}
                          </div>
                          
                          {item.collectionType === 'standalone' && item.productDetails?.shortHighlight && (
                            <p className="text-[#6B635D] text-sm line-clamp-2">
                              {item.productDetails.shortHighlight}
                            </p>
                          )}
                          
                          {item.collectionType !== 'standalone' && item.description && (
                            <p className="text-[#6B635D] text-sm line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPagesCalc > 1 && (
                <div className="flex justify-center items-center gap-4 mt-16">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2.5 border border-[#DBDBDB] hover:border-[#1C2A46] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5 text-[#1C2A46]" />
                  </button>
                  
                  <div className="flex gap-2">
                    {[...Array(Math.min(5, totalPagesCalc))].map((_, i) => {
                      let pageNum;
                      if (totalPagesCalc <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPagesCalc - 2) {
                        pageNum = totalPagesCalc - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-10 h-10 flex items-center justify-center ${
                            currentPage === pageNum
                              ? 'bg-[#55000C] text-white'
                              : 'text-[#1C2A46] hover:bg-white border border-[#DBDBDB]'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {totalPagesCalc > 5 && currentPage < totalPagesCalc - 2 && (
                      <>
                        <span className="w-10 h-10 flex items-center justify-center text-[#6B635D]">...</span>
                        <button
                          onClick={() => setCurrentPage(totalPagesCalc)}
                          className="w-10 h-10 flex items-center justify-center text-[#1C2A46] hover:bg-white border border-[#DBDBDB]"
                        >
                          {totalPagesCalc}
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(totalPagesCalc, prev + 1))}
                    disabled={currentPage === totalPagesCalc}
                    className="p-2.5 border border-[#DBDBDB] hover:border-[#1C2A46] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowRight className="w-5 h-5 text-[#1C2A46]" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Filter Modal */}
      <FilterModal 
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        productType={product.typeOfProduct}
      />

      {/* CSS for flip animation */}
      <style jsx>{`
        .flip-card {
          perspective: 1000px;
          position: relative;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }
        
        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          transition: transform 0.6s ease-in-out;
          transform-style: preserve-3d;
          border-radius: 2px;
        }
        
        .flip-card-front {
          transform: rotateY(0deg);
        }
        
        .flip-card-back {
          transform: rotateY(180deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .group:hover .flip-card-front {
          transform: rotateY(-180deg);
        }
        
        .group:hover .flip-card-back {
          transform: rotateY(0deg);
        }
        
        .flip-card-front, .flip-card-back {
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
};

export default ProductListing;